import { useState } from 'react'

/**
 * The `allowed_refs` input shared by the add and edit trust modals. Owns the raw text so a
 * half-typed list never round-trips through the parent as a broken array; emits `string[]`
 * (de-duplicated, trimmed) or `null` when "any ref" is ticked. Validation of the glob grammar is
 * the Worker's - its 400 message names the offending index - so this field only splits.
 */
export function splitRefs(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean))]
}

export function AllowedRefsField({ value, onChange, autoFocus = false }: {
  value: string[] | null
  onChange: (next: string[] | null) => void
  autoFocus?: boolean
}) {
  const [text, setText] = useState(value === null ? '' : value.join(', '))
  const anyRef = value === null

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 }}>Allowed refs</label>
      <input
        className="input"
        style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: '0.8rem' }}
        placeholder="main, release/*, tag:v*"
        value={text}
        disabled={anyRef}
        autoFocus={autoFocus}
        onChange={(e) => { setText(e.target.value); onChange(splitRefs(e.target.value)) }}
      />
      <p style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 6, marginBottom: 8 }}>
        Branches this pipeline may <strong>push</strong> from - state pushes, lock-file uploads and module publishes.
        Reads, locks and plans work from any branch, so PR pipelines keep working. <code>*</code> matches within
        one path segment; prefix a tag glob with <code>tag:</code>. Tokens carry no default-branch claim, so name it.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--text2)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={anyRef}
          onChange={(e) => onChange(e.target.checked ? null : splitRefs(text))}
        />
        Allow pushes from any ref
        <span className="badge badge-amber badge-still" style={{ fontSize: '0.62rem' }}>not recommended</span>
      </label>
    </div>
  )
}
